/**
 * ProjectWorkspace.jsx — Fiche projet unifiée
 * Regroupe Projet + Chef de projet + Objectifs + Tâches + KPI/Finances en un seul écran.
 * Pattern UI : Accordéons (sections dépliables) pour voir plusieurs zones simultanément.
 *
 * Sections :
 *   1. Liste des projets (sidebar gauche ou dropdown sélecteur)
 *   2. [A] Infos Projet + Chef de projet (obligatoire)
 *   3. [B] Objectifs principaux + breakdown mensuel/hebdo/quotidien
 *   4. [C] Tâches liées au projet / aux objectifs
 *   5. [D] KPI & Finances (tableau générique configurable)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Folder, Plus, Edit2, Trash2, ChevronDown, ChevronRight, ChevronUp,
  Users, Calendar, Target, CheckSquare, BarChart3, AlertTriangle,
  Save, X, User, DollarSign, Building, Clock, TrendingUp, RefreshCw,
  Hash, Type, List, ToggleLeft, Check, AlertCircle, ArrowRight,
  Settings, Table, Eye, EyeOff, GripVertical, Download, Copy, Archive,
  Search
} from 'lucide-react';
import { API_URL } from '../config';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => (n === null || n === undefined || n === '') ? '—' : Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

// ─── Export CSV helper ──────────────────────────────────────────────────────
const downloadCSV = (rows, headers, filename) => {
  const escape = (val) => {
    const str = val == null ? '' : String(val);
    return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const STATUS_TASK_COLORS = {
  'À faire':   'bg-elevated-1 text-text-secondary',
  'En cours':  'bg-blue-100 dark:bg-blue-900/40 text-blue-800',
  'En retard': 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200',
  'Terminé':   'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200',
  'Annulée':   'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
};

const STATUS_PROJECT_COLORS = {
  'En cours':   'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  'En attente': 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
  'Terminé':    'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  'Annulé':     'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  'Archivé':    'bg-elevated-1 text-text-muted',
};

const COL_TYPE_ICONS = {
  date:     <Calendar className="w-3 h-3" />,
  text:     <Type className="w-3 h-3" />,
  number:   <Hash className="w-3 h-3" />,
  currency: <DollarSign className="w-3 h-3" />,
  select:   <List className="w-3 h-3" />,
  boolean:  <ToggleLeft className="w-3 h-3" />,
};

// ─── Section Accordion wrapper ─────────────────────────────────────────────────
const Section = ({ id, title, icon: Icon, badge, open, onToggle, accent = 'blue', children }) => {
  const accentMap = {
    blue:   'border-blue-400 bg-blue-50 dark:bg-blue-950/40',
    violet: 'border-violet-400 bg-violet-50 dark:bg-violet-950/40',
    amber:  'border-amber-400 bg-amber-50 dark:bg-amber-950/40',
    emerald:'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/40',
    rose:   'border-rose-400 bg-rose-50 dark:bg-rose-950/40',
  };
  const headerMap = {
    blue:   'text-blue-700 dark:text-blue-300',
    violet: 'text-violet-700 dark:text-violet-300',
    amber:  'text-amber-700 dark:text-amber-300',
    emerald:'text-emerald-700 dark:text-emerald-300',
    rose:   'text-rose-700 dark:text-rose-300',
  };
  return (
    <div className={`bg-surface rounded-xl shadow border-l-4 ${accentMap[accent]} overflow-hidden mb-4`}>
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 sm:px-5 py-4 min-h-[44px] hover:bg-elevated-1 transition"
        onClick={() => onToggle(id)}
      >
        <div className="flex items-center gap-2 sm:gap-3">
          <Icon className={`w-5 h-5 ${headerMap[accent]}`} />
          <span className={`font-semibold text-sm sm:text-base ${headerMap[accent]}`}>{title}</span>
          {badge !== undefined && badge !== null && (
            <span className="bg-gray-200 text-text-secondary text-xs rounded-full px-2 py-0.5">{badge}</span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-text-muted" /> : <ChevronDown className="w-4 h-4 text-text-muted" />}
      </button>
      {open && <div className="px-3 sm:px-5 pb-4 sm:pb-5 pt-1 border-t border-border-subtle">{children}</div>}
    </div>
  );
};

// ─── ProjectWorkspace ──────────────────────────────────────────────────────────
const ProjectWorkspace = ({ employees = [], authToken, authUser, onNavigate }) => {
  // Permissions création/édition de projet
  const canEdit = authUser && ['Patron', 'Super Manager', 'Manager'].includes(authUser.role_name);

  // ── Filtres membres projet ──
  const [memberSearch, setMemberSearch] = useState('');
  const [memberServiceFilter, setMemberServiceFilter] = useState('');
  const [showExtras, setShowExtras] = useState(false);

  // Employés internes (sans extras) et extras séparés
  const internalEmployees = useMemo(() =>
    employees.filter(e => !e.deleted_at && e.role_name !== 'Extra'),
    [employees]
  );
  const extraEmployees = useMemo(() =>
    employees.filter(e => !e.deleted_at && e.role_name === 'Extra'),
    [employees]
  );

  // Filtrage par recherche et service (liste principale uniquement)
  const filteredInternals = useMemo(() => {
    let list = internalEmployees;
    if (memberServiceFilter) {
      list = list.filter(e => String(e.service_id) === memberServiceFilter);
    }
    if (memberSearch.trim()) {
      const q = memberSearch.toLowerCase().trim();
      list = list.filter(e => `${e.first_name} ${e.last_name}`.toLowerCase().includes(q));
    }
    return list;
  }, [internalEmployees, memberSearch, memberServiceFilter]);

  // ── State principal ──
  const [projects, setProjects]             = useState([]);
  const [selectedProject, setSelectedProject] = useState(null); // projet complet
  const [loading, setLoading]               = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [openSections, setOpenSections]     = useState({ project: true, objectives: false, tasks: false, timeline: false, kpi: false });
  const [activeTab, setActiveTab]           = useState('project'); // project | objectives | tasks | timeline | kpi
  const [showArchived, setShowArchived]     = useState(false);
  const [toast, setToast]                   = useState(null); // { type, msg }

  // ── Formulaire projet ──
  const emptyProjectForm = {
    name: '', description: '', start_date: '', end_date: '',
    status: 'En cours', client: '', budget: '',
    chef_de_projet_id: '', member_ids: [], service_id: '',
  };
  const [projectForm, setProjectForm]       = useState(emptyProjectForm);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [projectErrors, setProjectErrors]   = useState({});
  const [servicesList, setServicesList]     = useState([]);

  // ── Objectifs ──
  const [objectifs, setObjectifs]           = useState([]);
  const [showObjForm, setShowObjForm]       = useState(false);
  const [editingObj, setEditingObj]         = useState(null);
  const [objForm, setObjForm]               = useState({
    titre: '', description: '', date_debut: '', date_fin: '',
    chiffrage: '', unite: 'unités', valeur_actuelle: '', parent_objectif_id: null,
  });
  const [expandedObj, setExpandedObj]       = useState([]);

  // ── Tâches ──
  const [tasks, setTasks]                   = useState([]);
  const [showTaskForm, setShowTaskForm]     = useState(false);
  const [editingTask, setEditingTask]       = useState(null);
  const [taskForm, setTaskForm]             = useState({
    title: '', description: '', assigned_to: '', priority: 'Moyenne',
    due_date: '', due_time: '', status: 'À faire',
    project_id: '', objectif_id: '',
  });
  const [taskFilter, setTaskFilter]         = useState('all'); // all | project | objectif

  // ── KPI / Finances ──
  const [kpiColumns, setKpiColumns]         = useState([]);
  const [kpiRows, setKpiRows]               = useState([]);
  const [kpiTotals, setKpiTotals]           = useState([]);
  const [showColForm, setShowColForm]       = useState(false);
  const [editingCol, setEditingCol]         = useState(null);
  const [colForm, setColForm]               = useState({ label: '', col_type: 'text', options_json: '', position: 0 });
  const [pendingCells, setPendingCells]     = useState({}); // { 'rowId_colId': value }
  const [kpiObjectifs, setKpiObjectifs]     = useState([]);

  // ── Objectifs financiers ──
  const [financialGoals, setFinancialGoals] = useState([]);

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${authToken}`,
  }), [authToken]);

  // ── Toast helper ──
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Fermeture modales par Escape ──
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (showProjectForm) setShowProjectForm(false);
      else if (showObjForm) setShowObjForm(false);
      else if (showTaskForm) setShowTaskForm(false);
      else if (showColForm) setShowColForm(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  });

  // ── Toggle section ──
  const toggleSection = (id) => setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));

  // ─────────────────────────────────────────────────────────────────────────────
  // LOAD
  // ─────────────────────────────────────────────────────────────────────────────
  const loadProjects = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/projects`, { headers });
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  }, [headers]);

  const loadServices = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/services`, { headers });
      const data = await res.json();
      setServicesList(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  }, [headers]);

  const loadProjectDetails = useCallback(async (projectId) => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}`, { headers });
      if (!res.ok) throw new Error('Projet introuvable');
      const data = await res.json();
      setSelectedProject(data);
      // Aplatir récursivement : racines + sous-objectifs à profondeur N
      const roots = data.objectifs || [];
      const allObj = [];
      const flattenTree = (list) => {
        list.forEach(o => {
          allObj.push(o);
          if (o.sous_objectifs?.length) flattenTree(o.sous_objectifs);
        });
      };
      flattenTree(roots);
      setObjectifs(allObj);
      setTasks(data.tasks || []);
      // Load KPI columns & rows
      loadKpiColumns(projectId);
      loadKpiRows(projectId);
      // Load objectifs financiers
      loadFinancialGoals(projectId);
      // Load objectifs for KPI linking
      setKpiObjectifs(allObj);
    } catch (e) {
      console.error(e);
      // Retry once after 500ms (race condition: project may not be fully committed yet)
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        const retryRes = await fetch(`${API_URL}/projects/${projectId}`, { headers });
        if (!retryRes.ok) throw new Error('Projet introuvable après retry');
        const retryData = await retryRes.json();
        setSelectedProject(retryData);
        const retryRoots = retryData.objectifs || [];
        const retryAllObj = [];
        const flattenRetry = (list) => {
          list.forEach(o => {
            retryAllObj.push(o);
            if (o.sous_objectifs?.length) flattenRetry(o.sous_objectifs);
          });
        };
        flattenRetry(retryRoots);
        setObjectifs(retryAllObj);
        setTasks(retryData.tasks || []);
        loadKpiColumns(projectId);
        loadKpiRows(projectId);
        setKpiObjectifs(retryAllObj);
      } catch (retryErr) {
        console.error('Retry failed:', retryErr);
        showToast('Erreur chargement projet', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const loadKpiColumns = async (projId) => {
    try {
      const res = await fetch(`${API_URL}/projects/${projId}/kpi-columns`, { headers });
      const data = await res.json();
      setKpiColumns(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const loadKpiRows = async (projId) => {
    try {
      const res = await fetch(`${API_URL}/projects/${projId}/kpi-rows`, { headers });
      const data = await res.json();
      setKpiRows(Array.isArray(data) ? data : []);
      // Compute totals client-side for number/currency columns
      computeTotals(data);
    } catch (e) { console.error(e); }
  };

  const computeTotals = (rows) => {
    const totals = {};
    rows.forEach(row => {
      (row.cells || []).forEach(cell => {
        if (cell.col_type === 'number' || cell.col_type === 'currency') {
          totals[cell.col_id || cell.column_id] = (totals[cell.col_id || cell.column_id] || 0) + (parseFloat(cell.value_number) || 0);
        }
      });
    });
    setKpiTotals(totals);
  };

  // ── Chargement objectifs financiers ──
  const loadFinancialGoals = async (projId) => {
    try {
      const res = await fetch(`${API_URL}/projects/${projId}/financial-goals`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      setFinancialGoals(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  // ── Sauvegarder toutes les cellules KPI en attente ──
  const savePendingCells = async () => {
    const entries = Object.entries(pendingCells);
    if (entries.length === 0) return showToast('Aucune modification en attente', 'info');
    setSaving(true);
    try {
      for (const [key, value] of entries) {
        const [rowId, colId] = key.split('_');
        const col = kpiColumns.find(c => c.id === parseInt(colId));
        const body = { row_id: parseInt(rowId), column_id: parseInt(colId) };
        if (col?.col_type === 'boolean') body.value_boolean = value ? 1 : 0;
        else if (['number', 'currency'].includes(col?.col_type)) body.value_number = parseFloat(value) || 0;
        else body.value_text = String(value);
        await fetch(`${API_URL}/kpi-cells`, { method: 'PUT', headers, body: JSON.stringify(body) });
      }
      setPendingCells({});
      if (selectedProject) loadKpiRows(selectedProject.id);
      showToast(`${entries.length} cellule(s) enregistrée(s)`);
    } catch { showToast('Erreur de sauvegarde', 'error'); }
    setSaving(false);
  };

  useEffect(() => { loadProjects(); loadServices(); }, [loadProjects, loadServices]);

  // ─────────────────────────────────────────────────────────────────────────────
  // PROJECT CRUD
  // ─────────────────────────────────────────────────────────────────────────────
  const validateProject = (form) => {
    const errs = {};
    if (!form.name?.trim()) errs.name = 'Titre obligatoire';
    if (!form.chef_de_projet_id) errs.chef_de_projet_id = 'Chef de projet obligatoire';
    if (!form.start_date) errs.start_date = 'Date de début obligatoire';
    if (!form.end_date) errs.end_date = 'Date de fin obligatoire';
    if (form.start_date && form.end_date && form.start_date > form.end_date)
      errs.end_date = 'La date de fin doit être après le début';
    return errs;
  };

  const handleSubmitProject = async (e) => {
    e.preventDefault();
    const errs = validateProject(projectForm);
    if (Object.keys(errs).length > 0) { setProjectErrors(errs); return; }
    setProjectErrors({});
    setSaving(true);
    try {
      const url = editingProjectId ? `${API_URL}/projects/${editingProjectId}` : `${API_URL}/projects`;
      const method = editingProjectId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, headers,
        body: JSON.stringify({
          ...projectForm,
          budget: projectForm.budget ? parseFloat(projectForm.budget) : null,
          chef_de_projet_id: parseInt(projectForm.chef_de_projet_id),
          service_id: projectForm.service_id ? parseInt(projectForm.service_id) : null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      const newId = editingProjectId || data.id;
      await loadProjects();
      showToast(editingProjectId ? 'Projet modifié ✓' : 'Projet créé ✓');
      setShowProjectForm(false);
      setEditingProjectId(null);
      setProjectForm(emptyProjectForm);
      await loadProjectDetails(newId);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const openEditProject = (project) => {
    setEditingProjectId(project.id);
    setProjectForm({
      name: project.name || '',
      description: project.description || '',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      status: project.status || 'En cours',
      client: project.client || '',
      budget: project.budget || '',
      chef_de_projet_id: project.chef_de_projet_id || '',
      member_ids: project.member_ids || [],
      service_id: project.service_id || '',
    });
    setShowProjectForm(true);
    setOpenSections(prev => ({ ...prev, project: true }));
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Supprimer ce projet et toutes ses données ?')) return;
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Erreur suppression');
      await loadProjects();
      if (selectedProject?.id === projectId) {
        setSelectedProject(null);
        setObjectifs([]); setTasks([]); setKpiColumns([]); setKpiRows([]);
      }
      showToast('Projet supprimé');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const toggleMember = (empId) => {
    setProjectForm(prev => ({
      ...prev,
      member_ids: prev.member_ids.includes(empId)
        ? prev.member_ids.filter(id => id !== empId)
        : [...prev.member_ids, empId],
    }));
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // OBJECTIVES CRUD
  // ─────────────────────────────────────────────────────────────────────────────
  const validateObj = (form, project) => {
    const errs = {};
    if (!form.titre?.trim()) errs.titre = 'Titre obligatoire';
    if (!form.date_debut) errs.date_debut = 'Date début obligatoire';
    if (!form.date_fin) errs.date_fin = 'Date fin obligatoire';
    if (form.date_debut && form.date_fin && form.date_debut > form.date_fin)
      errs.date_fin = 'La date de fin doit être après le début';
    if (project) {
      if (form.date_debut && project.start_date && form.date_debut < project.start_date)
        errs.date_debut = `Doit être ≥ début projet (${fmtDate(project.start_date)})`;
      if (form.date_fin && project.end_date && form.date_fin > project.end_date)
        errs.date_fin = `Doit être ≤ fin projet (${fmtDate(project.end_date)})`;
    }
    return errs;
  };
  const [objErrors, setObjErrors] = useState({});

  const handleSubmitObj = async (e) => {
    e.preventDefault();
    const errs = validateObj(objForm, selectedProject);
    if (Object.keys(errs).length > 0) { setObjErrors(errs); return; }
    setObjErrors({});
    setSaving(true);
    try {
      const payload = {
        ...objForm,
        project_id: selectedProject.id,
        chiffrage: objForm.chiffrage ? parseFloat(objForm.chiffrage) : null,
        valeur_actuelle: objForm.valeur_actuelle ? parseFloat(objForm.valeur_actuelle) : 0,
        proprietaire_id: authUser?.employee_id || authUser?.id || selectedProject.chef_de_projet_id,
      };
      const url = editingObj ? `${API_URL}/objectifs/${editingObj}` : `${API_URL}/objectifs`;
      const method = editingObj ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      await loadProjectDetails(selectedProject.id);
      setShowObjForm(false);
      setEditingObj(null);
      setObjForm({ titre: '', description: '', date_debut: '', date_fin: '', chiffrage: '', unite: 'unités', valeur_actuelle: '', parent_objectif_id: null });
      showToast(editingObj ? 'Objectif modifié ✓' : 'Objectif créé ✓');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const openEditObj = (obj) => {
    setEditingObj(obj.id);
    setObjForm({
      titre: obj.titre || '',
      description: obj.description || '',
      date_debut: obj.date_debut || '',
      date_fin: obj.date_fin || '',
      chiffrage: obj.chiffrage || '',
      unite: obj.unite || 'unités',
      valeur_actuelle: obj.valeur_actuelle || '',
    });
    setShowObjForm(true);
  };

  const deleteObj = async (objId) => {
    if (!window.confirm('Supprimer cet objectif et sa décomposition ?')) return;
    try {
      const res = await fetch(`${API_URL}/objectifs/${objId}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Erreur suppression');
      await loadProjectDetails(selectedProject.id);
      showToast('Objectif supprimé');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const toggleObjExpand = (objId) => {
    setExpandedObj(prev => prev.includes(objId) ? prev.filter(id => id !== objId) : [...prev, objId]);
  };

  const updateRealValue = async (objId, val) => {
    try {
      await fetch(`${API_URL}/objectifs/${objId}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ valeur_actuelle: parseFloat(val) || 0 }),
      });
      setObjectifs(prev => prev.map(o => o.id === objId ? { ...o, valeur_actuelle: parseFloat(val) } : o));
    } catch (e) { console.error(e); }
  };

  // Agrégation progression sous-objectifs → parent
  const getAggregatedProgress = useCallback((parentId) => {
    const children = objectifs.filter(so => so.parent_objectif_id === parentId);
    if (children.length === 0) return null;
    const quantified = children.filter(c => c.chiffrage > 0);
    const completedCount = quantified.filter(c => (c.valeur_actuelle || 0) >= c.chiffrage).length;
    const avgPct = quantified.length > 0
      ? Math.round(quantified.reduce((sum, c) => sum + Math.min(100, Math.round(((c.valeur_actuelle || 0) / c.chiffrage) * 100)), 0) / quantified.length)
      : 0;
    return { total: children.length, quantified: quantified.length, completedCount, avgPct };
  }, [objectifs]);

  // ─────────────────────────────────────────────────────────────────────────────
  // EXPORT CSV
  // ─────────────────────────────────────────────────────────────────────────────
  const exportObjectifsCSV = () => {
    const hdrs = ['Titre', 'Description', 'Début', 'Fin', 'Chiffrage', 'Unité', 'Valeur actuelle', 'Progression %', 'Statut', 'Parent'];
    const rows = objectifs.map(o => {
      const chif = parseFloat(o.chiffrage) || 0;
      const pct = chif > 0 ? Math.min(100, Math.round(((o.valeur_actuelle || 0) / chif) * 100)) : (o.progression || 0);
      const parent = o.parent_objectif_id ? objectifs.find(p => p.id === o.parent_objectif_id)?.titre || '' : '';
      return [o.titre, o.description || '', o.date_debut || '', o.date_fin || '', o.chiffrage || '', o.unite || '', o.valeur_actuelle || 0, pct, o.statut || '', parent];
    });
    downloadCSV(rows, hdrs, `objectifs_${selectedProject?.name?.replace(/\s+/g, '_') || 'export'}.csv`);
    showToast('Export objectifs CSV téléchargé');
  };

  const exportTasksCSV = () => {
    const hdrs = ['Titre', 'Description', 'Assigné à', 'Statut', 'Priorité', 'Échéance', 'Objectif lié'];
    const rows = tasks.map(t => {
      const emp = employees.find(e => e.id === t.assigned_to);
      const obj = objectifs.find(o => o.id === t.objectif_id);
      return [t.title, t.description || '', emp ? `${emp.first_name} ${emp.last_name}` : '', t.status, t.priority || '', t.due_date || '', obj?.titre || ''];
    });
    downloadCSV(rows, hdrs, `taches_${selectedProject?.name?.replace(/\s+/g, '_') || 'export'}.csv`);
    showToast('Export tâches CSV téléchargé');
  };

  const exportKpiCSV = () => {
    const hdrs = ['Date', 'Label', ...kpiColumns.map(c => c.label)];
    const rows = kpiRows.map(r => {
      const cells = kpiColumns.map(col => {
        const cell = (r.cells || []).find(c => (c.col_id || c.column_id) === col.id);
        if (!cell) return '';
        if (col.col_type === 'boolean') return cell.value_boolean ? 'Oui' : 'Non';
        if (col.col_type === 'number' || col.col_type === 'currency') return cell.value_number ?? '';
        return cell.value_text || '';
      });
      return [r.row_date || '', r.label || '', ...cells];
    });
    downloadCSV(rows, hdrs, `kpi_${selectedProject?.name?.replace(/\s+/g, '_') || 'export'}.csv`);
    showToast('Export KPI CSV téléchargé');
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // TASKS CRUD
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSubmitTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title?.trim()) { showToast('Titre de tâche obligatoire', 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        ...taskForm,
        project_id: selectedProject?.id || null,
        assigned_to: taskForm.assigned_to ? parseInt(taskForm.assigned_to) : null,
        assigned_by: authUser?.employee_id || authUser?.id || null,
        objectif_id: taskForm.objectif_id ? parseInt(taskForm.objectif_id) : null,
      };
      const url = editingTask ? `${API_URL}/tasks/${editingTask}` : `${API_URL}/tasks`;
      const method = editingTask ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      if (selectedProject) await loadProjectDetails(selectedProject.id);
      setShowTaskForm(false);
      setEditingTask(null);
      setTaskForm({ title: '', description: '', assigned_to: '', priority: 'Moyenne', due_date: '', due_time: '', status: 'À faire', project_id: '', objectif_id: '' });
      showToast(editingTask ? 'Tâche modifiée ✓' : 'Tâche créée ✓');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      await fetch(`${API_URL}/tasks/${taskId}/status`, {
        method: 'PUT', headers,
        body: JSON.stringify({ status }),
      });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status } : t));
      showToast('Statut mis à jour');
      // Recharger les détails du projet pour rafraîchir les progressions objectifs
      if (selectedProject) {
        setTimeout(() => loadProjectDetails(selectedProject.id), 300);
      }
    } catch (e) { showToast(e.message, 'error'); }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    try {
      await fetch(`${API_URL}/tasks/${taskId}`, { method: 'DELETE', headers });
      setTasks(prev => prev.filter(t => t.id !== taskId));
      showToast('Tâche supprimée');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const openEditTask = (task) => {
    setEditingTask(task.id);
    setTaskForm({
      title: task.title || '',
      description: task.description || '',
      assigned_to: task.assigned_to || '',
      priority: task.priority || 'Moyenne',
      due_date: task.due_date || '',
      due_time: task.due_time || '',
      status: task.status || 'À faire',
      project_id: task.project_id || '',
      objectif_id: task.objectif_id || '',
    });
    setShowTaskForm(true);
  };

  const filteredTasks = useMemo(() => {
    if (taskFilter === 'project') return tasks.filter(t => !t.objectif_id);
    if (taskFilter === 'objectif') return tasks.filter(t => !!t.objectif_id);
    return tasks;
  }, [tasks, taskFilter]);

  // ── Drag & Drop statut tâche ──
  const [dragTaskId, setDragTaskId] = useState(null);

  const tasksByStatus = useMemo(() => {
    const groups = { 'À faire': [], 'En cours': [], 'En retard': [], 'Terminé': [], 'Annulée': [] };
    filteredTasks.forEach(t => { (groups[t.status] || groups['À faire']).push(t); });
    return groups;
  }, [filteredTasks]);

  const [taskViewMode, setTaskViewMode] = useState('list'); // list | kanban

  // ─────────────────────────────────────────────────────────────────────────────
  // KPI / FINANCES CRUD
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSubmitCol = async (e) => {
    e.preventDefault();
    if (!colForm.label?.trim() || !colForm.col_type) { showToast('Label et type obligatoires', 'error'); return; }
    setSaving(true);
    try {
      const url = editingCol ? `${API_URL}/kpi-columns/${editingCol}` : `${API_URL}/projects/${selectedProject.id}/kpi-columns`;
      const method = editingCol ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(colForm) });
      if (!res.ok) throw new Error((await res.json()).error || 'Erreur');
      await loadKpiColumns(selectedProject.id);
      setShowColForm(false);
      setEditingCol(null);
      setColForm({ label: '', col_type: 'text', options_json: '', position: 0 });
      showToast(editingCol ? 'Colonne modifiée ✓' : 'Colonne créée ✓');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteCol = async (colId) => {
    if (!window.confirm('Supprimer cette colonne (et toutes ses cellules) ?')) return;
    try {
      const res = await fetch(`${API_URL}/kpi-columns/${colId}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Erreur');
      await loadKpiColumns(selectedProject.id);
      await loadKpiRows(selectedProject.id);
      showToast('Colonne supprimée');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const addKpiRow = async () => {
    if (!selectedProject) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/projects/${selectedProject.id}/kpi-rows`, {
        method: 'POST', headers,
        body: JSON.stringify({ row_date: new Date().toISOString().split('T')[0], label: 'Nouvelle ligne' }),
      });
      if (!res.ok) throw new Error('Erreur ajout ligne');
      await loadKpiRows(selectedProject.id);
    } catch (e) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const deleteKpiRow = async (rowId) => {
    try {
      await fetch(`${API_URL}/kpi-rows/${rowId}`, { method: 'DELETE', headers });
      await loadKpiRows(selectedProject.id);
    } catch (e) { showToast(e.message, 'error'); }
  };

  const saveCells = async (rowId) => {
    const cells = Object.entries(pendingCells)
      .filter(([key]) => key.startsWith(`${rowId}_`))
      .map(([key, value]) => ({ column_id: parseInt(key.split('_')[1]), value }));
    if (cells.length === 0) return;
    try {
      await fetch(`${API_URL}/kpi-rows/${rowId}/cells`, {
        method: 'POST', headers,
        body: JSON.stringify({ cells }),
      });
      setPendingCells(prev => {
        const next = { ...prev };
        Object.keys(next).filter(k => k.startsWith(`${rowId}_`)).forEach(k => delete next[k]);
        return next;
      });
      await loadKpiRows(selectedProject.id);
      showToast('Cellules sauvegardées ✓');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const getCellValue = (row, colId) => {
    const key = `${row.id}_${colId}`;
    if (key in pendingCells) return pendingCells[key];
    const cell = (row.cells || []).find(c => (c.col_id || c.column_id) === colId);
    if (!cell) return '';
    const col = kpiColumns.find(c => c.id === colId);
    if (!col) return '';
    if (col.col_type === 'boolean') return cell.value_boolean ? '1' : '0';
    if (col.col_type === 'number' || col.col_type === 'currency') return cell.value_number ?? '';
    return cell.value_text ?? '';
  };

  const setCellPending = (rowId, colId, val) => {
    setPendingCells(prev => ({ ...prev, [`${rowId}_${colId}`]: val }));
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  const chefDeProjet = selectedProject
    ? employees.find(e => e.id === selectedProject.chef_de_projet_id)
    : null;

  return (
    <div className="flex flex-col gap-0 min-h-screen bg-elevated-1 pb-12">

      {/* ── Toast notification ── */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-xl shadow-xl text-white text-sm font-medium flex items-center gap-2 transition-all
          ${toast.type === 'error' ? 'bg-red-500' : 'bg-emerald-500'}`}>
          {toast.type === 'error' ? <AlertCircle className="w-4 h-4" /> : <Check className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          HEADER : sélecteur de projet + bouton nouveau
      ════════════════════════════════════════════════════════════════════════ */}
      <div className="bg-surface border-b shadow-sm px-3 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3">
          <Folder className="w-6 h-6 sm:w-7 sm:h-7 text-violet-600 dark:text-violet-400" />
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-text-primary">Projets &amp; Workspace</h1>
            <p className="text-xs text-text-muted hidden sm:block">Gérez projets, objectifs, tâches et KPI en un seul endroit</p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {/* Sélecteur projet */}
          <select
            className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-300 bg-surface min-w-0 w-full sm:w-auto sm:min-w-[200px] min-h-[44px]"
            value={selectedProject?.id || ''}
            onChange={async (e) => {
              const val = e.target.value;
              if (!val) { setSelectedProject(null); setObjectifs([]); setTasks([]); setKpiColumns([]); setKpiRows([]); }
              else await loadProjectDetails(parseInt(val));
            }}
          >
            <option value="">— Sélectionner un projet —</option>
            {projects.filter(p => showArchived || p.status !== 'Archivé').map(p => (
              <option key={p.id} value={p.id}>{p.name} {p.status === 'Archivé' ? '[Archivé]' : ''} {p.chef_de_projet_name ? `(${p.chef_de_projet_name})` : ''}</option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-text-muted cursor-pointer min-h-[44px]">
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} className="rounded text-violet-600 dark:text-violet-400 w-5 h-5 sm:w-4 sm:h-4" />
            <Archive className="w-3.5 h-3.5" /> Archivés
          </label>
          {canEdit && (
            <button
              className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium min-h-[44px] w-full sm:w-auto justify-center"
              onClick={() => {
                setEditingProjectId(null);
                setProjectForm(emptyProjectForm);
                setShowProjectForm(true);
                setOpenSections(prev => ({ ...prev, project: true }));
              }}
            >
              <Plus className="w-4 h-4" /> Nouveau projet
            </button>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          BODY
      ════════════════════════════════════════════════════════════════════════ */}
      <div className="max-w-5xl mx-auto w-full px-2 sm:px-4 pt-3 sm:pt-5">

        {/* ── Grille de cartes projet (avant sélection) ── */}
        {!selectedProject && !showProjectForm && projects.length > 0 && (() => {
          const visibleProjects = projects.filter(p => showArchived || p.status !== 'Archivé');
          return (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-text-secondary mb-3">Vos projets ({visibleProjects.length})</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {visibleProjects.map(p => (
                <button
                  key={p.id}
                  onClick={() => loadProjectDetails(p.id)}
                  className="bg-surface rounded-xl shadow-sm hover:shadow-md border border-border-subtle hover:border-violet-300 p-3 sm:p-4 text-left transition group min-h-[44px]"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-bold text-text-primary text-sm leading-tight group-hover:text-violet-700 dark:text-violet-300 truncate flex-1">{p.name}</h3>
                    <span className={`ml-2 flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_PROJECT_COLORS[p.status] || 'bg-elevated-1 text-text-secondary'}`}>
                      {p.status}
                    </span>
                  </div>
                  {p.description && <p className="text-xs text-text-muted mb-2 line-clamp-2">{p.description}</p>}
                  {p.chef_de_projet_name && (
                    <div className="flex items-center gap-1 text-xs text-text-secondary mb-1">
                      <User className="w-3 h-3" />
                      <span>{p.chef_de_projet_name}</span>
                    </div>
                  )}
                  {p.service_name && (
                    <div className="flex items-center gap-1 text-xs text-text-muted mb-1">
                      <Building className="w-3 h-3" />
                      <span>{p.service_name}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-text-muted">
                    {p.start_date && (
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(p.start_date)}</span>
                    )}
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{p.member_count || 0} membres</span>
                    {(p.objectif_count !== undefined && p.objectif_count > 0) && (
                      <span className="flex items-center gap-1"><Target className="w-3 h-3" />{p.objectif_count} obj.</span>
                    )}
                  </div>
                  {p.client && (
                    <div className="mt-1 text-xs text-text-muted">Client : {p.client}</div>
                  )}
                </button>
              ))}
            </div>
          </div>
          );
        })()}

        {/* ── Résumé projet actif ── */}
        {selectedProject && !showProjectForm && (
          <div className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl shadow-lg px-3 sm:px-6 py-3 sm:py-4 mb-4 sm:mb-5 flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-lg font-bold truncate">{selectedProject.name}</h2>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 border border-white/30`}>
                  {selectedProject.status}
                </span>
              </div>
              {selectedProject.description && (
                <p className="text-sm text-white/80 mt-1 truncate">{selectedProject.description}</p>
              )}
              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-white/80">
                {selectedProject.start_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {fmtDate(selectedProject.start_date)} → {fmtDate(selectedProject.end_date)}
                  </span>
                )}
                {selectedProject.client && (
                  <span className="flex items-center gap-1"><Building className="w-3 h-3" />{selectedProject.client}</span>
                )}
                {selectedProject.budget > 0 && (
                  <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{fmt(selectedProject.budget)} €</span>
                )}
                {selectedProject.service_name && (
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />Service : {selectedProject.service_name}</span>
                )}
                {/* Chef de projet badge */}
                <span className="flex items-center gap-1 font-semibold text-yellow-200">
                  <User className="w-3 h-3" />
                  Chef : {selectedProject.chef_de_projet_name || '(non défini)'}
                </span>
              </div>
            </div>
            <div className="flex gap-2 shrink-0 self-end sm:self-auto flex-wrap">
              {onNavigate && (
                <>
                  <button onClick={() => onNavigate('objectifs')}
                    className="bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg text-xs flex items-center gap-1.5"
                    title="Voir les objectifs">
                    <Target className="w-3.5 h-3.5" /> Objectifs
                  </button>
                  <button onClick={() => onNavigate('kpi')}
                    className="bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg text-xs flex items-center gap-1.5"
                    title="Voir les KPI">
                    <BarChart3 className="w-3.5 h-3.5" /> KPI
                  </button>
                </>
              )}
              <button
                className="bg-white/20 hover:bg-white/30 text-white p-2.5 sm:p-2 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
                title="Dupliquer le projet"
                onClick={async () => {
                  if (!window.confirm('Dupliquer ce projet avec ses membres et objectifs ?')) return;
                  try {
                    const res = await fetch(`${API_URL}/projects/${selectedProject.id}/duplicate`, { method: 'POST', headers });
                    const data = await res.json();
                    if (!res.ok) throw new Error(data.error);
                    await loadProjects();
                    await loadProjectDetails(data.id);
                    showToast('Projet dupliqué');
                  } catch (e) { showToast(e.message, 'error'); }
                }}
              >
                <Copy className="w-4 h-4" />
              </button>
              <button
                className="bg-white/20 hover:bg-white/30 text-white p-2.5 sm:p-2 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
                title={selectedProject.status === 'Archivé' ? 'Désarchiver' : 'Archiver le projet'}
                onClick={async () => {
                  const newStatus = selectedProject.status === 'Archivé' ? 'En cours' : 'Archivé';
                  try {
                    const res = await fetch(`${API_URL}/projects/${selectedProject.id}`, {
                      method: 'PUT', headers, body: JSON.stringify({ status: newStatus }),
                    });
                    if (!res.ok) throw new Error('Erreur');
                    await loadProjects();
                    await loadProjectDetails(selectedProject.id);
                    showToast(newStatus === 'Archivé' ? 'Projet archivé' : 'Projet réactivé');
                  } catch (e) { showToast(e.message, 'error'); }
                }}
              >
                <Archive className="w-4 h-4" />
              </button>
              <button
                className="bg-white/20 hover:bg-white/30 text-white p-2.5 sm:p-2 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
                title="Modifier le projet"
                onClick={() => openEditProject(selectedProject)}
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                className="bg-white/20 hover:bg-red-500 text-white p-2.5 sm:p-2 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
                title="Supprimer le projet"
                onClick={() => deleteProject(selectedProject.id)}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* No project selected placeholder */}
        {!selectedProject && !showProjectForm && (
          <div className="bg-surface rounded-xl shadow border border-dashed border-border-strong p-6 sm:p-12 text-center mb-5">
            <Folder className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-text-muted mb-4">
              {canEdit ? 'Sélectionnez un projet ou créez-en un nouveau' : 'Sélectionnez un projet pour voir les détails'}
            </p>
            {canEdit && (
              <button
                className="bg-violet-600 hover:bg-violet-700 text-white px-5 py-2 rounded-lg text-sm"
                onClick={() => { setProjectForm(emptyProjectForm); setShowProjectForm(true); setOpenSections(prev => ({ ...prev, project: true })); }}
              >
                Créer mon premier projet
              </button>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            SUB-TAB NAVIGATION
        ════════════════════════════════════════════════════════════════════════ */}
        {selectedProject && !showProjectForm && (
          <div className="bg-surface rounded-xl shadow-sm border mb-4 px-2 py-1 flex gap-1 overflow-x-auto scrollbar-hide flex-nowrap">
            {[
              { id: 'project', label: 'Projet', icon: Folder, activeClass: 'bg-violet-100 dark:bg-violet-900/40 text-violet-700', badgeActive: 'bg-violet-200 dark:bg-violet-800/40 text-violet-800 dark:text-violet-200' },
              { id: 'objectives', label: 'Objectifs', icon: Target, badge: objectifs.length, activeClass: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700', badgeActive: 'bg-amber-200 dark:bg-amber-800/40 text-amber-800 dark:text-amber-200' },
              { id: 'tasks', label: 'Tâches', icon: CheckSquare, badge: tasks.length, activeClass: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700', badgeActive: 'bg-emerald-200 dark:bg-emerald-800/40 text-emerald-800 dark:text-emerald-200' },
              { id: 'timeline', label: 'Timeline', icon: Clock, activeClass: 'bg-rose-100 dark:bg-rose-900/40 text-rose-700', badgeActive: 'bg-rose-200 dark:bg-rose-800/40 text-rose-800 dark:text-rose-200' },
              { id: 'kpi', label: 'KPI & Finances', icon: BarChart3, activeClass: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700', badgeActive: 'bg-blue-200 dark:bg-blue-800/40 text-blue-800 dark:text-blue-200' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setOpenSections(prev => {
                    const next = { project: false, objectives: false, tasks: false, timeline: false, kpi: false };
                    next[tab.id] = true;
                    return next;
                  });
                }}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap min-h-[44px]
                  ${activeTab === tab.id
                    ? `${tab.activeClass} shadow-sm`
                    : 'text-text-muted hover:bg-elevated-1 hover:text-text-secondary'
                  }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.badge > 0 && (
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.id ? tab.badgeActive : 'bg-gray-200 text-text-secondary'
                  }`}>{tab.badge}</span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            SECTION A — FORMULAIRE PROJET
        ═══════════════════════════════════════════════════════════════════════ */}
        {showProjectForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={e => e.target === e.currentTarget && (() => { setShowProjectForm(false); setEditingProjectId(null); setProjectErrors({}); })()}>
            <div className="w-full sm:max-w-2xl sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface shadow-2xl">
              <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
              <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
                <h2 className="text-lg sm:text-xl font-bold text-text-primary flex items-center gap-2">
                  <Folder className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                  {editingProjectId ? 'Modifier le projet' : 'Nouveau projet'}
                </h2>
                <button onClick={() => { setShowProjectForm(false); setEditingProjectId(null); setProjectErrors({}); }} className="p-2 hover:bg-elevated-1 rounded-lg">
                  <X className="w-5 h-5 text-text-muted" />
                </button>
              </div>

              <form onSubmit={handleSubmitProject} className="flex flex-col flex-1 overflow-hidden">
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                  {/* Titre */}
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Titre du projet <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={projectForm.name}
                      onChange={e => setProjectForm(p => ({ ...p, name: e.target.value }))}
                      className={`w-full border rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] focus:ring-2 focus:ring-violet-300 ${projectErrors.name ? 'border-red-400 dark:border-red-600' : 'border-border-strong'}`}
                      placeholder="Ex: Refonte site web, Événement gala..."
                    />
                    {projectErrors.name && <p className="text-red-500 text-xs mt-1">{projectErrors.name}</p>}
                  </div>

                  {/* Chef de projet (OBLIGATOIRE) */}
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Chef de projet <span className="text-red-500">*</span>
                      <span className="ml-2 text-xs text-text-muted font-normal">(obligatoire)</span>
                    </label>
                    <select
                      value={projectForm.chef_de_projet_id}
                      onChange={e => setProjectForm(p => ({ ...p, chef_de_projet_id: e.target.value }))}
                      className={`w-full border rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] focus:ring-2 focus:ring-violet-300 ${projectErrors.chef_de_projet_id ? 'border-red-400 dark:border-red-600' : 'border-border-strong'}`}
                    >
                      <option value="">— Sélectionner le chef de projet —</option>
                      {internalEmployees.map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name} {emp.service_name ? `— ${emp.service_name}` : ''}
                        </option>
                      ))}
                    </select>
                    {projectErrors.chef_de_projet_id && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />{projectErrors.chef_de_projet_id}
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Description</label>
                    <textarea
                      value={projectForm.description}
                      onChange={e => setProjectForm(p => ({ ...p, description: e.target.value }))}
                      rows={2}
                      className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] focus:ring-2 focus:ring-violet-300"
                      placeholder="Description du projet..."
                    />
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-1">Début <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        value={projectForm.start_date}
                        onChange={e => setProjectForm(p => ({ ...p, start_date: e.target.value }))}
                        className={`w-full border rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] focus:ring-2 focus:ring-violet-300 ${projectErrors.start_date ? 'border-red-400 dark:border-red-600' : 'border-border-strong'}`}
                      />
                      {projectErrors.start_date && <p className="text-red-500 text-xs mt-1">{projectErrors.start_date}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-1">Fin <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        value={projectForm.end_date}
                        onChange={e => setProjectForm(p => ({ ...p, end_date: e.target.value }))}
                        className={`w-full border rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] focus:ring-2 focus:ring-violet-300 ${projectErrors.end_date ? 'border-red-400 dark:border-red-600' : 'border-border-strong'}`}
                      />
                      {projectErrors.end_date && <p className="text-red-500 text-xs mt-1">{projectErrors.end_date}</p>}
                    </div>
                  </div>

                  {/* Client + Budget + Statut */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-1">Client</label>
                      <input
                        type="text"
                        value={projectForm.client}
                        onChange={e => setProjectForm(p => ({ ...p, client: e.target.value }))}
                        className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px]"
                        placeholder="Nom client"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-1">Budget (€)</label>
                      <input
                        type="number"
                        min={0}
                        value={projectForm.budget}
                        onChange={e => setProjectForm(p => ({ ...p, budget: e.target.value }))}
                        className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px]"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-1">Statut</label>
                      <select
                        value={projectForm.status}
                        onChange={e => setProjectForm(p => ({ ...p, status: e.target.value }))}
                        className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px]"
                      >
                        <option>En cours</option>
                        <option>En attente</option>
                        <option>Terminé</option>
                        <option>Annulé</option>
                      </select>
                    </div>
                  </div>

                  {/* Service associé (optionnel) */}
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Service associé (optionnel)</label>
                    <select
                      value={projectForm.service_id}
                      onChange={e => setProjectForm(p => ({ ...p, service_id: e.target.value }))}
                      className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] focus:ring-2 focus:ring-violet-300"
                    >
                      <option value="">— Aucun service —</option>
                      {servicesList.map(svc => (
                        <option key={svc.id} value={svc.id}>{svc.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Membres de l'équipe (internes) */}
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Membres de l'équipe
                      <span className="ml-2 text-xs text-text-muted font-normal">
                        {projectForm.member_ids.length} sélectionné(s)
                      </span>
                    </label>
                    <div className="border border-border-subtle rounded-lg bg-elevated-1 overflow-hidden">
                      {/* Barre de recherche */}
                      <div className="p-2 border-b bg-surface flex items-center gap-2">
                        <Search className="w-4 h-4 text-text-muted shrink-0" />
                        <input type="text" value={memberSearch}
                          onChange={e => setMemberSearch(e.target.value)}
                          placeholder="Rechercher par nom..."
                          className="w-full text-base sm:text-sm min-h-[44px] sm:min-h-0 border-none outline-none bg-transparent" />
                      </div>
                      {/* Filtre par service + actions groupées */}
                      <div className="p-2 border-b bg-surface flex items-center gap-2 flex-wrap">
                        <select value={memberServiceFilter}
                          onChange={e => setMemberServiceFilter(e.target.value)}
                          className="text-base sm:text-sm min-h-[44px] sm:min-h-0 border border-border-strong rounded-md px-2 py-1 bg-surface">
                          <option value="">Tous les services</option>
                          {servicesList.filter(s => s.name !== 'Extra').map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                        <button type="button" onClick={() => {
                          const ids = filteredInternals.map(e => e.id);
                          setProjectForm(p => ({ ...p, member_ids: [...new Set([...p.member_ids, ...ids])] }));
                        }}
                          className="text-xs text-violet-600 hover:text-violet-800 font-medium px-2 py-1 rounded hover:bg-violet-50 dark:bg-violet-950/40 transition">
                          Tout sélectionner
                        </button>
                        <button type="button" onClick={() => {
                          const ids = new Set(filteredInternals.map(e => e.id));
                          setProjectForm(p => ({ ...p, member_ids: p.member_ids.filter(id => !ids.has(id)) }));
                        }}
                          className="text-xs text-red-600 hover:text-red-800 font-medium px-2 py-1 rounded hover:bg-red-50 dark:bg-red-950/40 transition">
                          Tout désélectionner
                        </button>
                      </div>
                      {/* Liste des membres internes */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-52 overflow-y-auto p-2">
                        {filteredInternals.length === 0 ? (
                          <p className="text-sm text-text-muted col-span-2 text-center py-3">Aucun employé trouvé</p>
                        ) : filteredInternals.map(emp => (
                          <label key={emp.id} className="flex items-center gap-2 p-1.5 hover:bg-surface rounded cursor-pointer text-sm min-h-[44px] sm:min-h-0">
                            <input type="checkbox"
                              checked={projectForm.member_ids.includes(emp.id)}
                              onChange={() => toggleMember(emp.id)}
                              className="w-4 h-4 accent-violet-600" />
                            <div className="w-6 h-6 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 flex items-center justify-center text-xs font-bold shrink-0">
                              {emp.first_name?.[0]}{emp.last_name?.[0]}
                            </div>
                            <span className="truncate">{emp.first_name} {emp.last_name}</span>
                            {emp.service_name && <span className="text-xs text-text-muted ml-auto truncate hidden sm:inline">{emp.service_name}</span>}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Extras (liste secondaire repliable) */}
                  {extraEmployees.length > 0 && (
                    <div>
                      <button type="button" onClick={() => setShowExtras(!showExtras)}
                        className="flex items-center gap-2 text-sm font-medium text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:text-orange-300 mb-2">
                        {showExtras ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        Extras disponibles ({extraEmployees.length})
                        {projectForm.member_ids.filter(id => extraEmployees.some(e => e.id === id)).length > 0 && (
                          <span className="text-xs bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 px-1.5 py-0.5 rounded-full">
                            {projectForm.member_ids.filter(id => extraEmployees.some(e => e.id === id)).length} sélectionné(s)
                          </span>
                        )}
                      </button>
                      {showExtras && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-40 overflow-y-auto border border-orange-200 rounded-lg p-2 bg-orange-50 dark:bg-orange-950/40">
                          {extraEmployees.map(emp => (
                            <label key={emp.id} className="flex items-center gap-2 p-1.5 hover:bg-surface rounded cursor-pointer text-sm min-h-[44px] sm:min-h-0">
                              <input type="checkbox"
                                checked={projectForm.member_ids.includes(emp.id)}
                                onChange={() => toggleMember(emp.id)}
                                className="w-4 h-4 accent-orange-500" />
                              <div className="w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 flex items-center justify-center text-xs font-bold shrink-0">
                                {emp.first_name?.[0]}{emp.last_name?.[0]}
                              </div>
                              <span className="truncate">{emp.first_name} {emp.last_name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions — sticky footer */}
                <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
                  <button
                    type="button"
                    onClick={() => { setShowProjectForm(false); setEditingProjectId(null); setProjectErrors({}); }}
                    className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-gray-200 hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white flex items-center justify-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Enregistrement…' : (editingProjectId ? 'Modifier le projet' : 'Créer le projet')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            SECTION B — OBJECTIFS
        ═══════════════════════════════════════════════════════════════════════ */}
        {selectedProject && (
          <Section
            id="objectives"
            title="Objectifs"
            icon={Target}
            badge={objectifs.length}
            open={openSections.objectives}
            onToggle={toggleSection}
            accent="amber"
          >
            {/* Bouton ajouter objectif + export */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 pt-1">
              <p className="text-sm text-text-muted">Objectifs principaux du projet</p>
              <div className="flex items-center gap-2">
                {objectifs.length > 0 && (
                  <button
                    className="border border-amber-300 text-amber-700 hover:bg-amber-50 dark:bg-amber-950/40 px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm min-h-[44px]"
                    onClick={exportObjectifsCSV}
                    title="Exporter les objectifs en CSV"
                  >
                    <Download className="w-3.5 h-3.5" /> CSV
                  </button>
                )}
              <button
                className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm min-h-[44px]"
                onClick={() => {
                  setEditingObj(null);
                  setObjForm({
                    titre: '', description: '',
                    date_debut: selectedProject.start_date || '',
                    date_fin: selectedProject.end_date || '',
                    chiffrage: '', unite: 'unités', valeur_actuelle: '',
                  });
                  setShowObjForm(true);
                }}
              >
                <Plus className="w-4 h-4" /> Ajouter un objectif
              </button>
              </div>
            </div>

            {/* Modal objectif */}
            {showObjForm && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={e => e.target === e.currentTarget && (() => { setShowObjForm(false); setEditingObj(null); setObjErrors({}); })()}>
                <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface shadow-2xl">
                  <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
                  <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
                    <h4 className="font-semibold text-amber-800 dark:text-amber-200 text-sm sm:text-base">{editingObj ? 'Modifier objectif' : objForm.parent_objectif_id ? 'Nouveau sous-objectif' : 'Nouvel objectif'}</h4>
                    <button onClick={() => { setShowObjForm(false); setEditingObj(null); setObjErrors({}); }} className="p-2 hover:bg-elevated-1 rounded-lg">
                      <X className="w-5 h-5 text-text-muted" />
                    </button>
                  </div>

                  <form onSubmit={handleSubmitObj} className="flex flex-col flex-1 overflow-hidden">
                    <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                      <div>
                        <label className="text-xs font-medium text-text-secondary">Titre <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={objForm.titre}
                          onChange={e => setObjForm(p => ({ ...p, titre: e.target.value }))}
                          className={`w-full border rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1 ${objErrors.titre ? 'border-red-400 dark:border-red-600' : 'border-border-strong'}`}
                          placeholder="Titre de l'objectif"
                        />
                        {objErrors.titre && <p className="text-red-500 text-xs">{objErrors.titre}</p>}
                      </div>

                      <div>
                        <label className="text-xs font-medium text-text-secondary">Description</label>
                        <textarea
                          value={objForm.description}
                          onChange={e => setObjForm(p => ({ ...p, description: e.target.value }))}
                          rows={2}
                          className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1"
                          placeholder="Description..."
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-text-secondary">Début <span className="text-red-500">*</span></label>
                          <input
                            type="date"
                            value={objForm.date_debut}
                            min={selectedProject.start_date}
                            max={selectedProject.end_date}
                            onChange={e => setObjForm(p => ({ ...p, date_debut: e.target.value }))}
                            className={`w-full border rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1 ${objErrors.date_debut ? 'border-red-400 dark:border-red-600' : 'border-border-strong'}`}
                          />
                          {objErrors.date_debut && <p className="text-red-500 text-xs">{objErrors.date_debut}</p>}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-text-secondary">Fin <span className="text-red-500">*</span></label>
                          <input
                            type="date"
                            value={objForm.date_fin}
                            min={objForm.date_debut || selectedProject.start_date}
                            max={selectedProject.end_date}
                            onChange={e => setObjForm(p => ({ ...p, date_fin: e.target.value }))}
                            className={`w-full border rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1 ${objErrors.date_fin ? 'border-red-400 dark:border-red-600' : 'border-border-strong'}`}
                          />
                          {objErrors.date_fin && <p className="text-red-500 text-xs">{objErrors.date_fin}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs font-medium text-text-secondary">Résultat attendu</label>
                          <input
                            type="number"
                            value={objForm.chiffrage}
                            onChange={e => setObjForm(p => ({ ...p, chiffrage: e.target.value }))}
                            className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1"
                            placeholder="100"
                            min={0}
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-text-secondary">Unité</label>
                          <input
                            type="text"
                            value={objForm.unite}
                            onChange={e => setObjForm(p => ({ ...p, unite: e.target.value }))}
                            className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1"
                            placeholder="unités, €, %..."
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-text-secondary">Valeur actuelle</label>
                          <input
                            type="number"
                            value={objForm.valeur_actuelle}
                            onChange={e => setObjForm(p => ({ ...p, valeur_actuelle: e.target.value }))}
                            className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1"
                            placeholder="0"
                            min={0}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
                      <button
                        type="button"
                        onClick={() => { setShowObjForm(false); setEditingObj(null); setObjErrors({}); }}
                        className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-gray-200 hover:bg-gray-300"
                      >
                        Annuler
                      </button>
                      <button
                        type="submit"
                        disabled={saving}
                        className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white flex items-center justify-center gap-2"
                      >
                        <Save className="w-3 h-3" />
                        {saving ? 'Enregistrement…' : (editingObj ? 'Modifier' : 'Créer objectif')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Liste objectifs */}
            {objectifs.length === 0 && !showObjForm && (
              <div className="text-center py-6 text-text-muted text-sm">
                <Target className="w-8 h-8 mx-auto mb-2 opacity-40" />
                Aucun objectif pour ce projet
              </div>
            )}
            <div className="space-y-3">
              {objectifs.filter(o => !o.parent_objectif_id).map(obj => {
                const chiffrageNum = parseFloat(obj.chiffrage) || 0;
                const pct = chiffrageNum > 0
                  ? Math.min(100, Math.round(((obj.valeur_actuelle || 0) / chiffrageNum) * 100))
                  : (obj.progression || 0);
                const objTasks = tasks.filter(t => t.objectif_id === obj.id);
                const objTasksDone = objTasks.filter(t => t.status === 'Terminé').length;
                const taskPct = objTasks.length > 0 ? Math.round((objTasksDone / objTasks.length) * 100) : null;
                const hasChildren = objectifs.some(so => so.parent_objectif_id === obj.id);
                const isExpanded = expandedObj.includes(obj.id);
                return (
                  <div key={obj.id} className="border border-amber-100 rounded-xl bg-surface overflow-hidden">
                    {/* Header objectif */}
                    <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4">
                      <button
                        type="button"
                        onClick={() => toggleObjExpand(obj.id)}
                        className="mt-0.5 p-1 hover:bg-amber-50 dark:bg-amber-950/40 rounded"
                      >
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-amber-600 dark:text-amber-400" /> : <ChevronRight className="w-4 h-4 text-amber-600 dark:text-amber-400" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-text-primary text-sm">{obj.titre}</span>
                        </div>
                        {obj.description && <p className="text-xs text-text-muted mt-0.5 truncate">{obj.description}</p>}
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-text-muted">
                          <span><Calendar className="w-3 h-3 inline mr-1" />{fmtDate(obj.date_debut)} → {fmtDate(obj.date_fin)}</span>
                          {chiffrageNum > 0 && (
                            <span className="font-medium text-text-secondary">
                              Cible : {fmt(chiffrageNum)} {obj.unite}
                            </span>
                          )}
                        </div>
                        {/* Barre de progression + valeur réelle */}
                        {chiffrageNum > 0 && (
                          <div className="mt-2 flex items-center gap-3">
                            <div className="flex-1 bg-elevated-1 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full transition-all ${pct >= 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-400'}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-xs font-semibold text-text-secondary w-10 text-right">{pct}%</span>
                            <span className="text-xs text-text-muted">{fmt(obj.valeur_actuelle)} / {fmt(chiffrageNum)} {obj.unite}</span>
                          </div>
                        )}
                        {/* Barre progression tâches (quand pas de chiffrage) */}
                        {chiffrageNum === 0 && taskPct !== null && !hasChildren && (
                          <div className="mt-2">
                            <div className="flex items-center gap-3">
                              <div className="flex-1 bg-elevated-1 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full transition-all ${taskPct >= 100 ? 'bg-emerald-500' : taskPct >= 50 ? 'bg-blue-500' : 'bg-amber-400'}`}
                                  style={{ width: `${taskPct}%` }}
                                />
                              </div>
                              <span className="text-xs font-semibold text-text-secondary w-10 text-right">{taskPct}%</span>
                            </div>
                            <div className="flex items-center gap-1.5 mt-1 text-xs text-text-muted">
                              <CheckSquare className="w-3 h-3 text-blue-500" />
                              <span>{objTasksDone}/{objTasks.length} tâches terminées</span>
                            </div>
                          </div>
                        )}
                        {/* Barre agrégée depuis les sous-objectifs (parent sans cible propre) */}
                        {chiffrageNum === 0 && hasChildren && (() => {
                          const aggr = getAggregatedProgress(obj.id);
                          if (!aggr) return null;
                          return (
                            <div className="mt-2">
                              <div className="flex items-center gap-3">
                                <div className="flex-1 bg-elevated-1 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full transition-all ${aggr.avgPct >= 100 ? 'bg-emerald-500' : aggr.avgPct >= 50 ? 'bg-amber-500' : 'bg-red-400'}`}
                                    style={{ width: `${aggr.avgPct}%` }}
                                  />
                                </div>
                                <span className="text-xs font-semibold text-text-secondary w-10 text-right">{aggr.avgPct}%</span>
                              </div>
                              <div className="flex items-center gap-1.5 mt-1 text-xs text-text-muted">
                                <Check className="w-3 h-3 text-emerald-500" />
                                <span>{aggr.completedCount}/{aggr.quantified} sous-objectifs complétés</span>
                              </div>
                            </div>
                          );
                        })()}
                        {/* Editer valeur réelle inline (masqué si parent agrégé) */}
                        {(chiffrageNum > 0 || !hasChildren) && (
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-text-muted">Réel :</span>
                            <input
                              type="number"
                              defaultValue={obj.valeur_actuelle}
                              onBlur={e => updateRealValue(obj.id, e.target.value)}
                              className="border border-border-subtle rounded px-2 py-0.5 text-xs w-24 focus:ring-1 focus:ring-amber-300"
                              placeholder="0"
                            />
                            <span className="text-xs text-text-muted">{obj.unite}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingObj(null);
                            setObjForm({
                              titre: '', description: '',
                              date_debut: obj.date_debut || selectedProject.start_date || '',
                              date_fin: obj.date_fin || selectedProject.end_date || '',
                              chiffrage: '', unite: obj.unite || 'unités', valeur_actuelle: '',
                              parent_objectif_id: obj.id,
                            });
                            setShowObjForm(true);
                          }}
                          title="Ajouter un sous-objectif"
                          className="p-2.5 sm:p-1.5 text-green-600 hover:bg-green-50 dark:bg-green-950/40 rounded min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => openEditObj(obj)}
                          className="p-2.5 sm:p-1.5 text-blue-600 hover:bg-blue-50 dark:bg-blue-950/40 rounded min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteObj(obj.id)}
                          className="p-2.5 sm:p-1.5 text-red-500 hover:bg-red-50 dark:bg-red-950/40 rounded min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Sous-objectifs */}
                    {objectifs.filter(so => so.parent_objectif_id === obj.id).length > 0 && (
                      <div className="border-t border-blue-100 bg-blue-50/30 px-3 sm:px-4 py-3">
                        <h5 className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                          <Target className="w-3.5 h-3.5" /> Sous-objectifs
                          <span className="bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded-full text-[10px]">
                            {objectifs.filter(so => so.parent_objectif_id === obj.id).length}
                          </span>
                        </h5>
                        <div className="space-y-2">
                          {objectifs.filter(so => so.parent_objectif_id === obj.id).map(sub => {
                            const subPct = sub.chiffrage > 0 ? Math.min(100, Math.round((sub.valeur_actuelle / sub.chiffrage) * 100)) : 0;
                            return (
                              <div key={sub.id} className="bg-surface rounded-lg border border-blue-200 dark:border-blue-800 p-2.5 sm:p-3">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-semibold text-text-primary text-sm">{sub.titre}</span>
                                      {sub.chiffrage && (
                                        <span className="text-xs text-text-muted">Cible : {fmt(sub.chiffrage)} {sub.unite}</span>
                                      )}
                                    </div>
                                    {sub.description && <p className="text-xs text-text-muted mt-0.5">{sub.description}</p>}
                                    {sub.chiffrage > 0 && (
                                      <div className="mt-2 flex items-center gap-2">
                                        <div className="flex-1 bg-elevated-1 rounded-full h-1.5">
                                          <div className={`h-1.5 rounded-full transition-all ${subPct >= 100 ? 'bg-emerald-500' : subPct >= 50 ? 'bg-blue-500' : 'bg-amber-400'}`}
                                            style={{ width: `${subPct}%` }} />
                                        </div>
                                        <span className="text-xs font-medium text-text-secondary">{subPct}%</span>
                                      </div>
                                    )}
                                    <div className="flex items-center gap-2 mt-1.5">
                                      <span className="text-xs text-text-muted">Réel :</span>
                                      <input
                                        type="number"
                                        defaultValue={sub.valeur_actuelle}
                                        onBlur={e => updateRealValue(sub.id, e.target.value)}
                                        className="border border-border-subtle rounded px-2 py-0.5 text-xs w-24 focus:ring-1 focus:ring-blue-300"
                                        placeholder="0"
                                      />
                                      <span className="text-xs text-text-muted">{sub.unite}</span>
                                    </div>
                                  </div>
                                  <div className="flex gap-1 shrink-0">
                                    <button type="button" onClick={() => openEditObj(sub)}
                                      className="p-1 text-blue-600 hover:bg-blue-50 dark:bg-blue-950/40 rounded" title="Modifier">
                                      <Edit2 className="w-3.5 h-3.5" />
                                    </button>
                                    <button type="button" onClick={() => deleteObj(sub.id)}
                                      className="p-1 text-red-500 hover:bg-red-50 dark:bg-red-950/40 rounded" title="Supprimer">
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            SECTION C — TÂCHES
        ═══════════════════════════════════════════════════════════════════════ */}
        {selectedProject && (
          <Section
            id="tasks"
            title="Tâches"
            icon={CheckSquare}
            badge={tasks.length}
            open={openSections.tasks}
            onToggle={toggleSection}
            accent="emerald"
          >
            {/* Toolbar tâches */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pt-1">
              <div className="flex gap-2 overflow-x-auto scrollbar-hide flex-nowrap">
                {['all', 'project', 'objectif'].map(f => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setTaskFilter(f)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition whitespace-nowrap min-h-[44px] sm:min-h-0 ${taskFilter === f ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-surface text-text-secondary border-border-strong hover:border-emerald-400 dark:border-emerald-600'}`}
                  >
                    {f === 'all' ? 'Toutes' : f === 'project' ? 'Liées projet' : 'Liées objectif'}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                {tasks.length > 0 && (
                  <button
                    className="border border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:bg-emerald-950/40 px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm min-h-[44px]"
                    onClick={exportTasksCSV}
                    title="Exporter les tâches en CSV"
                  >
                    <Download className="w-3.5 h-3.5" /> CSV
                  </button>
                )}
                <button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm min-h-[44px]"
                  onClick={() => {
                    setEditingTask(null);
                    setTaskForm({ title: '', description: '', assigned_to: '', priority: 'Moyenne', due_date: '', due_time: '', status: 'À faire', project_id: selectedProject.id, objectif_id: '' });
                    setShowTaskForm(true);
                  }}
                >
                  <Plus className="w-4 h-4" /> Ajouter tâche
                </button>
              </div>
            </div>

            {/* Modal tâche */}
            {showTaskForm && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={e => e.target === e.currentTarget && (() => { setShowTaskForm(false); setEditingTask(null); })()}>
                <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface shadow-2xl">
                  <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
                  <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
                    <h4 className="font-semibold text-emerald-800 dark:text-emerald-200 text-sm sm:text-base">{editingTask ? 'Modifier tâche' : 'Nouvelle tâche'}</h4>
                    <button onClick={() => { setShowTaskForm(false); setEditingTask(null); }} className="p-2 hover:bg-elevated-1 rounded-lg">
                      <X className="w-5 h-5 text-text-muted" />
                    </button>
                  </div>

                  <form onSubmit={handleSubmitTask} className="flex flex-col flex-1 overflow-hidden">
                    <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                      <div>
                        <label className="text-xs font-medium text-text-secondary">Titre <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={taskForm.title}
                          onChange={e => setTaskForm(p => ({ ...p, title: e.target.value }))}
                          className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1"
                          placeholder="Titre de la tâche"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-text-secondary">Description</label>
                        <textarea
                          value={taskForm.description}
                          onChange={e => setTaskForm(p => ({ ...p, description: e.target.value }))}
                          rows={2}
                          className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-text-secondary">Assigné à</label>
                          <select
                            value={taskForm.assigned_to}
                            onChange={e => setTaskForm(p => ({ ...p, assigned_to: e.target.value }))}
                            className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1"
                          >
                            <option value="">— Personne —</option>
                            {employees.map(emp => (
                              <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-text-secondary">Priorité</label>
                          <select
                            value={taskForm.priority}
                            onChange={e => setTaskForm(p => ({ ...p, priority: e.target.value }))}
                            className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1"
                          >
                            <option>Faible</option><option>Moyenne</option><option>Haute</option><option>Critique</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-text-secondary">Date limite</label>
                          <input
                            type="date"
                            value={taskForm.due_date}
                            onChange={e => setTaskForm(p => ({ ...p, due_date: e.target.value }))}
                            className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-text-secondary">Objectif lié</label>
                          <select
                            value={taskForm.objectif_id}
                            onChange={e => setTaskForm(p => ({ ...p, objectif_id: e.target.value }))}
                            className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1"
                          >
                            <option value="">— Projet uniquement —</option>
                            {objectifs.map(o => (
                              <option key={o.id} value={o.id}>{o.titre}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-text-secondary">Statut</label>
                        <select
                          value={taskForm.status}
                          onChange={e => setTaskForm(p => ({ ...p, status: e.target.value }))}
                          className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1"
                        >
                          <option>À faire</option>
                          <option>En cours</option>
                          <option>En retard</option>
                          <option>Terminé</option>
                          <option>Annulée</option>
                        </select>
                      </div>
                    </div>

                    <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
                      <button
                        type="button"
                        onClick={() => { setShowTaskForm(false); setEditingTask(null); }}
                        className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-gray-200 hover:bg-gray-300"
                      >
                        Annuler
                      </button>
                      <button
                        type="submit"
                        disabled={saving}
                        className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white flex items-center justify-center gap-2"
                      >
                        <Save className="w-3 h-3" />
                        {saving ? 'Enregistrement…' : (editingTask ? 'Modifier' : 'Créer tâche')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Toggle vue liste / kanban */}
            <div className="flex items-center gap-2 mb-3">
              <button
                onClick={() => setTaskViewMode('list')}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${taskViewMode === 'list' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-surface text-text-secondary border-border-strong hover:border-emerald-400 dark:border-emerald-600'}`}
              >Liste</button>
              <button
                onClick={() => setTaskViewMode('kanban')}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${taskViewMode === 'kanban' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-surface text-text-secondary border-border-strong hover:border-emerald-400 dark:border-emerald-600'}`}
              >Kanban</button>
            </div>

            {filteredTasks.length === 0 && !showTaskForm && (
              <div className="text-center py-6 text-text-muted text-sm">
                <CheckSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
                Aucune tâche dans cette vue
              </div>
            )}

            {/* Vue KANBAN (drag & drop) */}
            {taskViewMode === 'kanban' && filteredTasks.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2 min-h-[200px]">
                {Object.entries(tasksByStatus).map(([status, statusTasks]) => (
                  <div
                    key={status}
                    className="bg-elevated-1 rounded-lg p-2 min-h-[180px]"
                    onDragOver={e => e.preventDefault()}
                    onDrop={async (e) => {
                      e.preventDefault();
                      const taskId = parseInt(e.dataTransfer.getData('text/plain'));
                      if (taskId && tasks.find(t => t.id === taskId)?.status !== status) {
                        await updateTaskStatus(taskId, status);
                        if (selectedProject) await loadProjectDetails(selectedProject.id);
                      }
                      setDragTaskId(null);
                    }}
                  >
                    <div className={`text-[10px] font-bold uppercase tracking-wide mb-2 px-1 ${
                      status === 'Terminé' ? 'text-green-700 dark:text-green-300' : status === 'En retard' ? 'text-red-700 dark:text-red-300' :
                      status === 'En cours' ? 'text-blue-700 dark:text-blue-300' : status === 'Annulée' ? 'text-orange-700 dark:text-orange-300' : 'text-text-secondary'
                    }`}>
                      {status} ({statusTasks.length})
                    </div>
                    <div className="space-y-1.5">
                      {statusTasks.map(task => {
                        const assignee = employees.find(e => e.id === task.assigned_to);
                        return (
                          <div
                            key={task.id}
                            draggable
                            onDragStart={e => { e.dataTransfer.setData('text/plain', String(task.id)); setDragTaskId(task.id); }}
                            onDragEnd={() => setDragTaskId(null)}
                            className={`bg-surface rounded-lg p-2 border border-border-subtle shadow-sm cursor-grab active:cursor-grabbing text-xs hover:shadow-md transition ${
                              dragTaskId === task.id ? 'opacity-50' : ''
                            }`}
                          >
                            <p className="font-medium text-text-primary truncate">{task.title}</p>
                            {assignee && <p className="text-text-muted mt-0.5 truncate">{assignee.first_name} {assignee.last_name?.charAt(0)}.</p>}
                            {task.due_date && <p className="text-text-muted mt-0.5">{fmtDate(task.due_date)}</p>}
                            <div className="flex gap-1 mt-1">
                              <button onClick={() => openEditTask(task)} className="p-0.5 text-blue-500 hover:bg-blue-50 dark:bg-blue-950/40 rounded"><Edit2 className="w-3 h-3" /></button>
                              <button onClick={() => deleteTask(task.id)} className="p-0.5 text-red-400 hover:bg-red-50 dark:bg-red-950/40 rounded"><Trash2 className="w-3 h-3" /></button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Vue LISTE */}
            {taskViewMode === 'list' && (
            <div className="space-y-2">
              {filteredTasks.map(task => {
                const assignee = employees.find(e => e.id === task.assigned_to);
                const linkedObj = objectifs.find(o => o.id === task.objectif_id);
                return (
                  <div key={task.id} className="bg-surface border border-border-subtle rounded-lg p-3 sm:p-3 hover:shadow-sm transition">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-text-primary text-sm">{task.title}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_TASK_COLORS[task.status] || 'bg-elevated-1 text-text-secondary'}`}>
                            {task.status}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            task.priority === 'Critique' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300' :
                            task.priority === 'Haute' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' :
                            task.priority === 'Moyenne' ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300' :
                            'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                          }`}>{task.priority}</span>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-xs text-text-muted">
                          {assignee && <span>{assignee.first_name} {assignee.last_name}</span>}
                          {task.due_date && <span>{fmtDate(task.due_date)}</span>}
                          {linkedObj && <span className="text-amber-600 dark:text-amber-400">{linkedObj.titre}</span>}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0 self-end sm:self-auto">
                        <select
                          value={task.status}
                          onChange={async e => { await updateTaskStatus(task.id, e.target.value); if (selectedProject) await loadProjectDetails(selectedProject.id); }}
                          className="text-xs border border-border-subtle rounded px-1 py-1 min-h-[44px] sm:min-h-0 sm:py-0.5"
                          title="Changer le statut"
                        >
                          <option>À faire</option><option>En cours</option><option>En retard</option>
                          <option>Terminé</option><option>Annulée</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => openEditTask(task)}
                          className="p-2.5 sm:p-1.5 text-blue-600 hover:bg-blue-50 dark:bg-blue-950/40 rounded min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteTask(task.id)}
                          className="p-2.5 sm:p-1.5 text-red-500 hover:bg-red-50 dark:bg-red-950/40 rounded min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </Section>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            SECTION — TIMELINE
        ═══════════════════════════════════════════════════════════════════════ */}
        {selectedProject && (
          <Section
            id="timeline"
            title="Timeline du projet"
            icon={Clock}
            badge={objectifs.length + tasks.length}
            open={openSections.timeline}
            onToggle={toggleSection}
            accent="rose"
          >
            {(() => {
              if (!selectedProject.start_date || !selectedProject.end_date) {
                return <p className="text-sm text-text-muted text-center py-4">Définissez les dates du projet pour voir la timeline.</p>;
              }
              const projStart = new Date(selectedProject.start_date);
              const projEnd = new Date(selectedProject.end_date);
              const totalDays = Math.max(1, (projEnd - projStart) / 86400000);
              const today = new Date();
              const todayPct = Math.min(100, Math.max(0, ((today - projStart) / 86400000 / totalDays) * 100));

              const getBar = (startDate, endDate, label, color, pct) => {
                const s = startDate ? new Date(startDate) : projStart;
                const e = endDate ? new Date(endDate) : projEnd;
                const left = Math.max(0, ((s - projStart) / 86400000 / totalDays) * 100);
                const width = Math.max(2, ((e - s) / 86400000 / totalDays) * 100);
                return { left, width, label, color, pct };
              };

              // Projet global
              const projBar = getBar(selectedProject.start_date, selectedProject.end_date, selectedProject.name, 'bg-violet-500', null);

              // Objectifs racine
              const objBars = objectifs.filter(o => !o.parent_objectif_id).map(obj => {
                const chiffrageNum = parseFloat(obj.chiffrage) || 0;
                const pct = chiffrageNum > 0
                  ? Math.min(100, Math.round(((obj.valeur_actuelle || 0) / chiffrageNum) * 100))
                  : (obj.progression || 0);
                return getBar(obj.date_debut, obj.date_fin, obj.titre, 'bg-amber-500', pct);
              });

              // Tâches avec dates
              const taskBars = tasks.filter(t => t.due_date).slice(0, 15).map(t => {
                const isDone = t.status === 'Terminé';
                return getBar(t.created_at?.split('T')[0], t.due_date, t.title,
                  isDone ? 'bg-emerald-400' : t.status === 'En retard' ? 'bg-red-400' : 'bg-blue-400',
                  isDone ? 100 : 0);
              });

              // Mois labels
              const monthLabels = [];
              const mCurrent = new Date(projStart.getFullYear(), projStart.getMonth(), 1);
              while (mCurrent <= projEnd) {
                const pct = ((mCurrent - projStart) / 86400000 / totalDays) * 100;
                if (pct >= 0 && pct <= 100) {
                  monthLabels.push({ label: mCurrent.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }), pct });
                }
                mCurrent.setMonth(mCurrent.getMonth() + 1);
              }

              const renderBars = (bars, rowH = 'h-5') => bars.map((b, i) => (
                <div key={i} className="relative w-full" style={{ height: '28px' }}>
                  <div
                    className={`absolute ${rowH} rounded-full ${b.color} opacity-80`}
                    style={{ left: `${b.left}%`, width: `${b.width}%` }}
                    title={`${b.label} ${b.pct != null ? `(${b.pct}%)` : ''}`}
                  >
                    {b.pct != null && b.pct > 0 && (
                      <div className="h-full rounded-full bg-white/30" style={{ width: `${b.pct}%` }} />
                    )}
                  </div>
                  <span className="absolute text-[10px] text-text-secondary truncate max-w-[140px]"
                    style={{ left: `${Math.min(b.left, 70)}%`, top: '6px', paddingLeft: b.width > 20 ? '6px' : `${b.width + 1}%` }}>
                    {b.label} {b.pct != null ? `${b.pct}%` : ''}
                  </span>
                </div>
              ));

              return (
                <div className="space-y-4 overflow-x-auto">
                  {/* En-tête mois */}
                  <div className="relative h-6 border-b border-border-subtle">
                    {monthLabels.map((m, i) => (
                      <span key={i} className="absolute text-[10px] text-text-muted -translate-x-1/2"
                        style={{ left: `${m.pct}%` }}>
                        {m.label}
                      </span>
                    ))}
                    {/* Ligne du jour */}
                    <div className="absolute top-0 bottom-0 w-px bg-red-400 z-10" style={{ left: `${todayPct}%` }}>
                      <span className="absolute -top-4 -translate-x-1/2 text-[9px] text-red-500 font-medium whitespace-nowrap">Auj.</span>
                    </div>
                  </div>

                  {/* Projet */}
                  <div>
                    <p className="text-[10px] text-violet-600 dark:text-violet-400 font-semibold uppercase tracking-wide mb-1">Projet</p>
                    {renderBars([projBar])}
                  </div>

                  {/* Objectifs */}
                  {objBars.length > 0 && (
                    <div>
                      <p className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold uppercase tracking-wide mb-1">Objectifs ({objBars.length})</p>
                      {renderBars(objBars)}
                    </div>
                  )}

                  {/* Tâches */}
                  {taskBars.length > 0 && (
                    <div>
                      <p className="text-[10px] text-blue-600 dark:text-blue-400 font-semibold uppercase tracking-wide mb-1">Tâches ({taskBars.length})</p>
                      {renderBars(taskBars, 'h-3')}
                    </div>
                  )}

                  {/* Légende */}
                  <div className="flex flex-wrap gap-3 text-[10px] text-text-muted border-t border-border-subtle pt-2">
                    <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-full bg-violet-500" /> Projet</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-full bg-amber-500" /> Objectifs</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-full bg-blue-400" /> Tâches</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-full bg-emerald-400" /> Terminé</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-full bg-red-400" /> En retard</span>
                    <span className="flex items-center gap-1"><span className="w-px h-3 bg-red-400" /> Aujourd'hui</span>
                  </div>
                </div>
              );
            })()}
          </Section>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            SECTION D — KPI & FINANCES
        ═══════════════════════════════════════════════════════════════════════ */}
        {selectedProject && (
          <Section
            id="kpi"
            title="KPI & Finances"
            icon={BarChart3}
            badge={kpiRows.length > 0 ? `${kpiRows.length} lignes` : null}
            open={openSections.kpi}
            onToggle={toggleSection}
            accent="rose"
          >
            {/* Toolbar KPI */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pt-1">
              <div className="flex gap-2 flex-wrap">
                <button
                  className="bg-rose-500 hover:bg-rose-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm min-h-[44px]"
                  onClick={() => {
                    setEditingCol(null);
                    setColForm({ label: '', col_type: 'text', options_json: '', position: kpiColumns.length });
                    setShowColForm(true);
                  }}
                >
                  <Settings className="w-4 h-4" /> Configurer colonnes
                </button>
                <button
                  className="bg-surface hover:bg-elevated-1 text-text-secondary border border-border-strong px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm min-h-[44px]"
                  onClick={addKpiRow}
                  disabled={kpiColumns.length === 0}
                  title={kpiColumns.length === 0 ? 'Créez d\'abord des colonnes' : 'Ajouter une ligne'}
                >
                  <Plus className="w-4 h-4" /> Ajouter ligne
                </button>
                {Object.keys(pendingCells).length > 0 && (
                  <button
                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm min-h-[44px]"
                    onClick={savePendingCells}
                    disabled={saving}
                  >
                    <Save className="w-3.5 h-3.5" /> Enregistrer ({Object.keys(pendingCells).length})
                  </button>
                )}
                {kpiRows.length > 0 && (
                  <button
                    className="border border-rose-300 text-rose-700 hover:bg-rose-50 dark:bg-rose-950/40 px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm"
                    onClick={exportKpiCSV}
                    title="Exporter la table KPI en CSV"
                  >
                    <Download className="w-3.5 h-3.5" /> Export CSV
                  </button>
                )}
              </div>
            </div>

            {/* Modal config colonnes */}
            {showColForm && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={e => e.target === e.currentTarget && (() => { setShowColForm(false); setEditingCol(null); })()}>
                <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface shadow-2xl">
                  <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
                  <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
                    <h4 className="font-semibold text-rose-800 dark:text-rose-200 text-sm sm:text-base">{editingCol ? 'Modifier colonne' : 'Nouvelle colonne'}</h4>
                    <button onClick={() => { setShowColForm(false); setEditingCol(null); }} className="p-2 hover:bg-elevated-1 rounded-lg">
                      <X className="w-5 h-5 text-text-muted" />
                    </button>
                  </div>

                  <form onSubmit={handleSubmitCol} className="flex flex-col flex-1 overflow-hidden">
                    <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-text-secondary">Label <span className="text-red-500">*</span></label>
                          <input
                            type="text"
                            value={colForm.label}
                            onChange={e => setColForm(p => ({ ...p, label: e.target.value }))}
                            className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1"
                            placeholder="Ex: Chiffre d'affaires"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-text-secondary">Type</label>
                          <select
                            value={colForm.col_type}
                            onChange={e => setColForm(p => ({ ...p, col_type: e.target.value }))}
                            className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1"
                          >
                            <option value="text">Texte</option>
                            <option value="number">Nombre</option>
                            <option value="currency">Devise (€)</option>
                            <option value="date">Date</option>
                            <option value="boolean">Oui/Non</option>
                            <option value="select">Liste (select)</option>
                          </select>
                        </div>
                      </div>
                      {colForm.col_type === 'select' && (
                        <div>
                          <label className="text-xs font-medium text-text-secondary">Options (JSON ou virgule-séparées)</label>
                          <input
                            type="text"
                            value={colForm.options_json}
                            onChange={e => setColForm(p => ({ ...p, options_json: e.target.value }))}
                            className="w-full border border-border-strong rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1"
                            placeholder='["Option1","Option2"] ou Option1,Option2'
                          />
                        </div>
                      )}
                      {/* Liste des colonnes existantes */}
                      {kpiColumns.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-text-secondary mb-2">Colonnes existantes :</p>
                          <div className="flex flex-wrap gap-2">
                            {kpiColumns.map(col => (
                              <div key={col.id} className="flex items-center gap-1.5 bg-surface border border-border-subtle rounded-lg px-2 py-1 text-xs min-h-[36px]">
                                {COL_TYPE_ICONS[col.col_type]}
                                <span>{col.label}</span>
                                <button
                                  type="button"
                                  onClick={() => { setEditingCol(col.id); setColForm({ label: col.label, col_type: col.col_type, options_json: col.options_json || '', position: col.position }); }}
                                  className="text-blue-500 hover:text-blue-700 dark:text-blue-300 ml-1 p-1"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteCol(col.id)}
                                  className="text-red-400 hover:text-red-600 dark:text-red-400 p-1"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
                      <button
                        type="button"
                        onClick={() => { setShowColForm(false); setEditingCol(null); }}
                        className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-gray-200 hover:bg-gray-300"
                      >
                        Fermer
                      </button>
                      <button
                        type="submit"
                        disabled={saving}
                        className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white"
                      >
                        {saving ? 'Enregistrement…' : (editingCol ? 'Modifier' : 'Créer colonne')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Tableau KPI */}
            {kpiColumns.length === 0 ? (
              <div className="text-center py-8 text-text-muted text-sm">
                <Table className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p>Aucune colonne configurée</p>
                <p className="text-xs mt-1">Cliquez sur « Configurer colonnes » pour commencer</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-border-subtle -mx-1 sm:mx-0">
                <table className="w-full text-xs sm:text-sm min-w-[600px]">
                  <thead>
                    <tr className="bg-elevated-1 border-b border-border-subtle">
                      <th className="text-left px-3 py-2 text-xs font-semibold text-text-secondary w-36">Objectif lié</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-text-secondary w-28">Date</th>
                      {kpiColumns.map(col => (
                        <th key={col.id} className="text-left px-3 py-2 text-xs font-semibold text-text-secondary min-w-[100px]">
                          <div className="flex items-center gap-1">
                            {COL_TYPE_ICONS[col.col_type]}
                            {col.label}
                          </div>
                        </th>
                      ))}
                      <th className="w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {kpiRows.map((row, rowIdx) => (
                      <tr key={row.id} className={`border-b border-border-subtle hover:bg-elevated-1 ${rowIdx % 2 === 0 ? '' : 'bg-elevated-1/30'}`}>
                        {/* Objectif lié */}
                        <td className="px-3 py-2">
                          <select
                            value={row.objectif_id || ''}
                            onChange={async (e) => {
                              try {
                                await fetch(`${API_URL}/kpi-rows/${row.id}`, {
                                  method: 'PUT', headers,
                                  body: JSON.stringify({ objectif_id: e.target.value || null }),
                                });
                                await loadKpiRows(selectedProject.id);
                              } catch (err) { console.error(err); }
                            }}
                            className="text-xs border border-border-subtle rounded px-1 py-0.5 w-full max-w-[130px]"
                          >
                            <option value="">Projet</option>
                            {kpiObjectifs.map(o => (
                              <option key={o.id} value={o.id}>{o.titre}</option>
                            ))}
                          </select>
                        </td>
                        {/* Date */}
                        <td className="px-3 py-2">
                          <input
                            type="date"
                            defaultValue={row.row_date || ''}
                            onBlur={async (e) => {
                              try {
                                await fetch(`${API_URL}/kpi-rows/${row.id}`, {
                                  method: 'PUT', headers,
                                  body: JSON.stringify({ row_date: e.target.value }),
                                });
                              } catch (err) { console.error(err); }
                            }}
                            className="text-xs border border-border-subtle rounded px-1 py-0.5 w-28"
                          />
                        </td>
                        {/* Cellules */}
                        {kpiColumns.map(col => {
                          const val = getCellValue(row, col.id);
                          const isPending = `${row.id}_${col.id}` in pendingCells;
                          return (
                            <td key={col.id} className="px-3 py-2">
                              {col.col_type === 'boolean' ? (
                                <input
                                  type="checkbox"
                                  checked={val === '1' || val === true}
                                  onChange={e => { setCellPending(row.id, col.id, e.target.checked ? '1' : '0'); saveCells(row.id); }}
                                  className="w-4 h-4 accent-rose-500"
                                />
                              ) : col.col_type === 'select' ? (
                                <select
                                  value={val}
                                  onChange={e => { setCellPending(row.id, col.id, e.target.value); saveCells(row.id); }}
                                  className="text-xs border border-border-subtle rounded px-1 py-0.5 w-full"
                                >
                                  <option value="">—</option>
                                  {(() => {
                                    try {
                                      const opts = col.options_json
                                        ? (col.options_json.trim().startsWith('[') ? JSON.parse(col.options_json) : col.options_json.split(',').map(s => s.trim()))
                                        : [];
                                      return opts.map((o, i) => <option key={i} value={o}>{o}</option>);
                                    } catch { return []; }
                                  })()}
                                </select>
                              ) : (
                                <input
                                  type={col.col_type === 'date' ? 'date' : col.col_type === 'number' || col.col_type === 'currency' ? 'number' : 'text'}
                                  value={val}
                                  onChange={e => setCellPending(row.id, col.id, e.target.value)}
                                  onBlur={() => { if (isPending) saveCells(row.id); }}
                                  className={`text-xs border rounded px-2 py-1 w-full focus:ring-1 focus:ring-rose-300 ${isPending ? 'border-rose-300 bg-rose-50 dark:bg-rose-950/40' : 'border-border-subtle'}`}
                                  placeholder={col.col_type === 'currency' ? '0.00' : ''}
                                  step={col.col_type === 'currency' ? '0.01' : undefined}
                                />
                              )}
                            </td>
                          );
                        })}
                        {/* Actions */}
                        <td className="px-2 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => deleteKpiRow(row.id)}
                            className="p-1 text-red-400 hover:bg-red-50 dark:bg-red-950/40 rounded"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Totaux */}
                  {kpiColumns.some(c => c.col_type === 'number' || c.col_type === 'currency') && (
                    <tfoot>
                      <tr className="bg-elevated-1 border-t-2 border-border-strong">
                        <td colSpan={2} className="px-3 py-2 text-xs font-semibold text-text-secondary">Total</td>
                        {kpiColumns.map(col => (
                          <td key={col.id} className="px-3 py-2 text-xs font-bold text-text-primary">
                            {(col.col_type === 'number' || col.col_type === 'currency')
                              ? (col.col_type === 'currency'
                                ? `${fmt(kpiTotals[col.id] || 0)} €`
                                : fmt(kpiTotals[col.id] || 0))
                              : ''}
                          </td>
                        ))}
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}

            {/* Objectifs financiers intégrés */}
            {financialGoals.length > 0 && (
              <div className="mt-6 border-t border-border-subtle pt-4">
                <h4 className="font-semibold text-text-primary mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Objectifs Financiers
                </h4>
                <div className="space-y-3">
                  {financialGoals.map(g => {
                    const totalSigne = (g.signatures || []).reduce((s, sig) => s + (sig.montant_ht || 0), 0);
                    const pctGoal = g.target_amount > 0 ? Math.round((totalSigne / g.target_amount) * 100) : 0;
                    return (
                      <div key={g.id} className="bg-elevated-1 border border-border-subtle rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="font-medium text-text-primary">{g.label}</span>
                            <span className="text-xs text-text-muted ml-2">{g.category}</span>
                          </div>
                          <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{fmt(totalSigne)} / {fmt(g.target_amount)} €</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div className={`h-2 rounded-full transition-all ${pctGoal >= 80 ? 'bg-emerald-500' : pctGoal >= 50 ? 'bg-amber-500' : 'bg-red-400'}`}
                              style={{ width: `${Math.min(100, pctGoal)}%` }} />
                          </div>
                          <span className="text-xs font-medium text-text-secondary w-10 text-right">{pctGoal}%</span>
                        </div>
                        {g.sub_objectives?.length > 0 && (
                          <div className="mt-2 text-xs text-text-muted">
                            {g.sub_objectives.length} sous-objectif(s) opérationnel(s)
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {onNavigate && (
                  <button onClick={() => onNavigate('kpi')}
                    className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:text-blue-300 flex items-center gap-1">
                    Voir le détail KPI complet <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
          </Section>
        )}

      </div>

      {/* Indicateur de chargement */}
      {loading && (
        <div className="fixed bottom-4 right-4 bg-surface shadow-xl rounded-xl px-4 py-2 flex items-center gap-2 text-sm text-text-secondary">
          <RefreshCw className="w-4 h-4 animate-spin text-violet-500" />
          Chargement…
        </div>
      )}
    </div>
  );
};

export default ProjectWorkspace;
